import React, { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { login } from "@/services/shared/auth/auth.service";
import { Link } from "react-router-dom";
import StarterContainer from "../../components/StarterContainer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MailIcon } from "lucide-react";
import { FormError } from "@/components/FormError";
import { useAppNavigate } from "@/lib/AppNavigatorHook";
import { ErrorBox } from "@/components/ui/error-box";
import { useLoading } from "../../lib/LoadingHook";
import { Button } from "@/components/ui/button";
import { isUnauthorized, isForbidden } from "@/lib/errorUtils";
import { AxiosError } from "axios";
import {
  RedirectToHomeIfLoggedIn,
  usePrivate,
} from "@/contexts/PrivateContext";
import { PasswordInput } from "@/components/ui/password-input";
interface LoginFormInputs {
  email: string;
  password: string;
}

const schema = yup.object().shape({
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required"),
  password: yup
    .string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

export function LoginPage() {
  const appNavigate = useAppNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInputs>({
    resolver: yupResolver(schema),
  });

  const priv = usePrivate();

  const { isLoading, whenLoading } = useLoading();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const onSubmit: SubmitHandler<LoginFormInputs> = async (data) => {
    const success = await whenLoading(async () => {
      try {
        await login(data.email, data.password);
        return true;
      } catch (error) {
        if (isForbidden(error)) {
          const msg =
            error instanceof AxiosError &&
            error.response?.data &&
            typeof error.response.data === "object" &&
            "message" in error.response.data &&
            typeof (error.response.data as { message?: string }).message === "string"
              ? (error.response.data as { message: string }).message
              : null;
          setErrorMessage(
            msg ?? "E-mail não confirmado. Verifique sua caixa de entrada pelo link de confirmação."
          );
        } else if (isUnauthorized(error)) {
          setErrorMessage("Usuário ou Senha inválidos");
        } else {
          setErrorMessage(
            error instanceof Error ? error.message : "Erro ao registrar usuário"
          );
        }
      }

      return false;
    });

    if (success) {
      priv.refreshData();
      appNavigate.goToHome();
    }
  };

  return (
    <StarterContainer>
      <RedirectToHomeIfLoggedIn />

      <h2 className="text-2xl font-bold ">Log in</h2>
      <p className="mb-4">Seja bem-vindo!</p>
      <ErrorBox message={errorMessage} />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="block font-medium">
            Email
          </Label>
          <div className="relative flex items-center">
            <MailIcon className="absolute left-3 text-muted-foreground h-5 w-5" />
            <Input
              id="email"
              placeholder="Insira seu email"
              type="email"
              className="pl-10"
              {...register("email")}
            />
          </div>
          <FormError message={errors.email?.message} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="block font-medium">
            Senha
          </Label>
          <PasswordInput
            id="password"
            placeholder="Senha"
            {...register("password")}
          />
          <FormError message={errors.password?.message} />
        </div>
        <Button
          id="login-button"
          type="submit"
          className="w-full bg-black text-white py-2 rounded-md"
          isLoading={isLoading}
        >
          Entrar
        </Button>
      </form>

      <div className="text-center mt-4">
        <p className="text-sm">
          Não possui conta?{" "}
          <Link to="/signup" className="text-primary font-bold hover:underline">
            Criar Conta
          </Link>
        </p>
        <p className="text-sm mt-2">
          Esqueceu a senha?{" "}
          <Link
            to="/forgot-password"
            className="text-primary font-bold hover:underline"
          >
            Clique aqui
          </Link>
        </p>
      </div>
    </StarterContainer>
  );
}
